import styled from '@emotion/styled'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  LoadingSpinner,
  SimpleTabStrip,
  Space,
  TabContent,
  ValidationMessage
} from '@/components/core-v2'
import StepTab, { StepStateName } from '@/components/core-v2/molecules/step-tab'
import TradeSubmittedModal from '@/components/core-v2/organisms/trade-submitted-modal'
import { PATH } from '@/const'
import { UserDisplayInfo } from '@/types/user'

import {
  actions as builderActions,
  StepName
} from '../../../tradebuilder-v2/modules/00-builder/state/builder-slice'
import { useAppDispatch, useAppSelector } from '../../../tradebuilder-v2/store/hooks'
import { TradeActivationStep } from '../../../tradebuilder-v2/types'
import TradeTopupWithdraw from '../01-topup-withdraw/pages/trade-topup-withdraw'
import { TradeEditPortfolioStep } from '../02-portfolio/TradeEditPortfolioStep'
import { TradeEditStrategyStep } from '../03-strategy/TradeEditStrategyStep'
import { TradeEditCompleteStep } from '../04-complete/TradeEditComplete'

export type TradeEditorTabsProps = {
  accountId: string
  userInfo: UserDisplayInfo
}

const StyledWrapper = styled.div`
  background-color: var(--White);
`

const StyledTabsWrapper = styled.div`
  border-bottom: 1px solid var(--White80);
`

const TradeEditorTabs = ({ accountId, userInfo }: TradeEditorTabsProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedStep, setSelectedStep] = useState<StepName>(undefined)
  const [completedStep, setCompletedStep] = useState<StepName>(undefined)
  const dispatch = useAppDispatch()

  const termsState = useAppSelector(state => state.terms)
  const builderState = useAppSelector(state => state.builder)
  const completionState = useAppSelector(state => state.complete)
  const requiresSettlement = termsState && termsState.terms.requiresSettlement
  // for withdrawal-only, activation process is not required
  const hasWithdrawal =
    builderState.updatedAccountPendingState &&
    builderState.updatedAccountPendingState.withdrawal !== undefined
  const hasOtherEdits =
    (completionState && completionState.approvals.portfolio) || completionState.approvals.strategy
  const updateDoesNotRequireFullApproval = hasWithdrawal && !hasOtherEdits

  const stepsRequired = requiresSettlement
    ? ['Submit', 'Economics', 'Approval', 'Commitment', 'Settlement', 'Activate']
    : ['Submit', 'Economics', 'Approval', 'Commitment', 'Activate']

  const stepMap = useMemo(() => new Map<StepName, number>(), [])
  stepMap.set('topupWithdraw', 0)
  stepMap.set('portfolio', 1)
  stepMap.set('strategy', 2)
  stepMap.set('complete', 3)

  const navigate = useNavigate()

  useEffect(() => {
    setIsLoading(false)
    const currentStep = builderState.step
    const stepIndex = stepMap.get(currentStep)

    if (stepIndex === undefined || stepIndex <= 0) {
      setSelectedStep('topupWithdraw')
      setCompletedStep('topupWithdraw')
    } else {
      setCompletedStep(currentStep)
      setSelectedStep(currentStep)
    }
  }, [builderState, stepMap])

  useEffect(() => {
    dispatch(
      builderActions.init({
        accountId,
        userName: userInfo.userName,
        userRoles: userInfo.userRoles,
        userType: userInfo.userType
      })
    )
  }, [dispatch, accountId, userInfo.userName, userInfo.userRoles, userInfo.userType])

  useEffect(() => {
    const isCurrentlyEditing =
      builderState.status === TradeActivationStep.draft ||
      builderState.status === TradeActivationStep.active ||
      builderState.status === TradeActivationStep.rejected ||
      builderState.status === TradeActivationStep.expired
    if (builderState.selectedAccount && (builderState.isFinished || !isCurrentlyEditing)) {
      dispatch(builderActions.setApprovalNotification({ title: 'Trade approved' }))
    }
  }, [
    builderState.selectedAccount,
    builderState.isFinished,
    builderState.status,
    builderState.selectedAccountId,
    dispatch,
    navigate
  ])

  const onNotificationClose = () => {
    dispatch(builderActions.resetNotification())
    navigate(PATH.trades)
  }

  const getState = (stepName: StepName): StepStateName => {
    const stepIndex = stepMap.get(stepName)
    if (stepName === completedStep) return 'current'
    const isCompleted = stepIndex >= stepMap.get(completedStep)
    return isCompleted ? 'pending' : 'complete'
  }
  const renderTab = (tabName: string, stepNumber: number, stepName: StepName) => (
    <StepTab
      isSelected={selectedStep === stepName}
      state={getState(stepName)}
      stepNumber={stepNumber}
      onClick={() => setSelectedStep(stepName)}
    >
      {tabName}
    </StepTab>
  )

  return (
    <>
      <StyledWrapper>
        <StyledTabsWrapper>
          <Space isflex flexDirection="row" px={5}>
            <SimpleTabStrip showRule={false}>
              {renderTab('Top up or withdraw', 1, 'topupWithdraw')}
              {renderTab('Portfolio', 2, 'portfolio')}
              {renderTab('Strategy', 3, 'strategy')}
              {renderTab('Complete', 4, 'complete')}
            </SimpleTabStrip>
          </Space>
        </StyledTabsWrapper>
      </StyledWrapper>
      <TabContent>
        <LoadingSpinner contextName="editing status" show={isLoading} />
        {builderState.errors && (
          <ValidationMessage errors={builderState.errors} excludeFieldNames={['collateral']} />
        )}
        {selectedStep === 'topupWithdraw' && <TradeTopupWithdraw />}
        {selectedStep === 'portfolio' && <TradeEditPortfolioStep />}
        {selectedStep === 'strategy' && <TradeEditStrategyStep />}
        {selectedStep === 'complete' && <TradeEditCompleteStep />}
      </TabContent>
      {builderState &&
        builderState.approvalNotification &&
        (updateDoesNotRequireFullApproval ? (
          <TradeSubmittedModal
            acceptCaption="Back to all live trades"
            open={builderState.approvalNotification !== undefined}
            steps={['Submit', 'Commitment', 'Activate']}
            onAccept={() => onNotificationClose()}
          />
        ) : (
          <TradeSubmittedModal
            acceptCaption="Back to all live trades"
            open={builderState.approvalNotification !== undefined}
            steps={stepsRequired}
            onAccept={() => onNotificationClose()}
          />
        ))}
    </>
  )
}

export default TradeEditorTabs
